import type { components } from "@/core/api/schema";
import { WEEK } from "@/features/brands/brand-rules-form";
import { inSlotOrder } from "@/features/brands/meal-slot-form";

export type AdminSubscription = components["schemas"]["AdminSubscription"];
export type AdminSubscriptionDetail = components["schemas"]["AdminSubscriptionDetail"];
export type SubscriptionStatus = AdminSubscription["status"];
export type MealSlot = AdminSubscription["slots"][number];
export type Meal = components["schemas"]["Meal"];
export type MealStatus = Meal["status"];

type Tone = "neutral" | "success" | "info" | "warn" | "danger";

export const SUBSCRIPTION_STATUS_DISPLAY: Record<SubscriptionStatus, { label: string; tone: Tone }> = {
  SCHEDULED: { label: "Starts soon", tone: "info" },
  ACTIVE: { label: "Active", tone: "success" },
  COMPLETED: { label: "Completed", tone: "neutral" },
  CANCELLED: { label: "Cancelled", tone: "danger" },
};

export const SOURCE_DISPLAY: Record<AdminSubscription["source"], string> = {
  ONLINE: "Paid online",
  OFFLINE: "Paid offline",
  ADMIN: "Free plan from Brocoly",
};

export { inSlotOrder };

/** Each brand names its own slots; they come in the brand's order of a day. */
export const formatSlots = (slots: readonly MealSlot[]) =>
  inSlotOrder(slots)
    .map((s) => s.name)
    .join(", ");

/** "Every day", "Mon–Sat", "Mon, Wed, Fri": runs of three or more days shown as a range. */
export function formatDays(days: readonly number[]): string {
  const set = new Set(days);
  if (set.size === 7) return "Every day";
  const ordered = WEEK.filter((w) => set.has(w.day));
  const parts: string[] = [];
  let run: typeof ordered = [];
  const flush = () => {
    if (run.length >= 3) parts.push(`${run[0]!.label}–${run[run.length - 1]!.label}`);
    else parts.push(...run.map((w) => w.label));
    run = [];
  };
  for (const w of ordered) {
    const previous = run[run.length - 1];
    if (previous && WEEK.indexOf(w) !== WEEK.indexOf(previous) + 1) flush();
    run.push(w);
  }
  flush();
  return parts.join(", ");
}

/** Where the plan's next meal goes (a customer can send single meals to another saved address). */
export function nextAddress(subscription: { meals: readonly Meal[] }, today: string): string | null {
  const meal =
    subscription.meals.find((m) => m.date >= today && m.deliveryAddress) ??
    subscription.meals.find((m) => m.deliveryAddress);
  const a = meal?.deliveryAddress;
  return a ? [a.line1, a.line2, a.city, a.pincode].filter(Boolean).join(", ") : null;
}

/** Every meal status in words, with a tone that also differs in lightness (readable without colour). */
export const MEAL_STATUS_DISPLAY: Record<MealStatus, { label: string; tone: Tone }> = {
  SCHEDULED: { label: "Scheduled", tone: "neutral" },
  PAUSED: { label: "Paused", tone: "warn" },
  AUTO_PAUSED: { label: "Auto-paused", tone: "warn" },
  FORFEITED: { label: "Forfeited", tone: "neutral" },
  MISSED_BY_BRAND: { label: "Missed by kitchen", tone: "danger" },
  DISPATCHED: { label: "On the way", tone: "info" },
  DELIVERED: { label: "Delivered", tone: "success" },
  RECEIVED: { label: "Received", tone: "success" },
  DISPUTED: { label: "Disputed", tone: "danger" },
  CANCELLED: { label: "Cancelled", tone: "neutral" },
};

export const PAUSE_KIND_LABEL: Record<NonNullable<Meal["pauseKind"]>, string> = {
  FULL_DAY: "whole day paused by the customer",
  SINGLE_MEAL: "paused by the customer",
  AUTO: "no dish chosen in time",
  HOLIDAY: "kitchen holiday",
  MENU_CHANGED: "chosen dish taken off the menu (no pause used)",
};

/** Plan meals need no tag; moved and bonus meals do. */
export const MEAL_SOURCE_TAG: Record<Meal["source"], string | null> = {
  PLAN: null,
  RESCHEDULED: "Moved",
  BONUS: "Bonus",
};

/** Plans that can still change (bonus meals, cancelling). */
export const isOpen = (status: SubscriptionStatus) => status === "SCHEDULED" || status === "ACTIVE";

export const SUBSCRIPTION_LIMITS = {
  bonusMealsPerRequest: 30,
  reasonMin: 3,
  reasonMax: 300,
  mealRangeDays: 62,
  /** Meals one removal may take off; the most meals left a plan can be set to (subscriptions.constant.ts). */
  removePerRequest: 60,
  mealsLeftMax: 500,
} as const;
